/**
 * Admin Dashboard Component
 * Main dashboard interface for administrators.
 */

import { PortalCard } from '../shell/portal-card';
import { Card } from '../../components/card';
import { useTranslation } from 'react-i18next';
import { useHistoryLocation } from '../../hooks/use-history-location';
import { useAuthStore } from '../../services/authentication/auth-store';
import { CommonHeader } from '../shell/common-header';
import { UserManagement } from './user-management';
import { FamilyManagement } from './family-management';
import { CohortManagement } from './cohort-management';
import { WorldSessionManagement } from './session-management';
import { GameManagement } from './game-management';
import { ModeratorDashboard, type ModeratorView } from '../moderator/moderator-dashboard';
import { LaunchMatch } from './launch-match';
import { JsonImporter } from './bulk-import/json-importer';
import { CampaignManagement } from './campaigns/campaign-management';
import styles from '../shell/portal-dashboard.module.css';

type AdminView =
  | 'main' | 'users' | 'families' | 'cohorts' | 'sessions' | 'games'
  | 'moderation' | 'match-moderation' | 'evaluation' | 'crash-support'
  | 'launch-match' | 'json-importer' | 'campaigns';
type AdminLocation = { view: AdminView; campaignId?: string };

export function AdminDashboard() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { location, navigate, back } = useHistoryLocation<AdminLocation>({ view: 'main' });
  const open = (view: AdminView) => navigate({ view });

  if (location.view === 'users') {
    return <UserManagement onBack={back} />;
  }
  if (location.view === 'families') {
    return <FamilyManagement onBack={back} />;
  }
  if (location.view === 'cohorts') {
    return <CohortManagement onBack={back} />;
  }
  if (location.view === 'sessions') {
    return <WorldSessionManagement onBack={back} />;
  }
  if (location.view === 'games') {
    return <GameManagement onBack={back} />;
  }
  if (location.view === 'moderation' || location.view === 'match-moderation' || location.view === 'evaluation' || location.view === 'crash-support') {
    const sub: ModeratorView = location.view === 'moderation' ? 'main' : location.view;
    return (
      <ModeratorDashboard
        subView={sub}
        onOpen={(view) => navigate({ view })}
        onBack={back}
        showMainBack={sub === 'main'}
      />
    );
  }
  if (location.view === 'launch-match') {
    return <LaunchMatch onBack={back} />;
  }
  if (location.view === 'json-importer') {
    return <JsonImporter onBack={back} />;
  }
  if (location.view === 'campaigns') {
    return (
      <CampaignManagement
        campaignId={location.campaignId ?? null}
        onOpenCampaign={(campaignId) => navigate({ view: 'campaigns', campaignId })}
        onBack={back}
      />
    );
  }

  return (
    <div className="portal-shell portal-container">
      <CommonHeader title={t('admin.dashboard.title')} />

      <main className="portal-main">
        <div className="portal-content">
          <Card framed padded={false} className={styles.portalWelcome}>
            <h2 className={styles.portalWelcomeTitle}>
              {t('admin.dashboard.welcome', { name: user?.name })}
            </h2>
            <p className={styles.portalWelcomeSubtitle}>
              {t('admin.dashboard.subtitle')}
            </p>
          </Card>

          <div className={styles.portalGrid}>
            <PortalCard
              title={t('admin.dashboard.cards.users.title')}
              description={t('admin.dashboard.cards.users.description')}
              icon="👥"
              onClick={() => open('users')}
            />
            <PortalCard
              title={t('admin.dashboard.cards.families.title')}
              description={t('admin.dashboard.cards.families.description')}
              icon="👨‍👩‍👧‍👦"
              onClick={() => open('families')}
            />
            <PortalCard
              title={t('admin.dashboard.cards.cohorts.title')}
              description={t('admin.dashboard.cards.cohorts.description')}
              icon="🏫"
              onClick={() => open('cohorts')}
            />
            <PortalCard
              title={t('admin.dashboard.cards.sessions.title')}
              description={t('admin.dashboard.cards.sessions.description')}
              icon="📅"
              onClick={() => open('sessions')}
            />
            <PortalCard
              title={t('admin.dashboard.cards.campaigns.title')}
              description={t('admin.dashboard.cards.campaigns.description')}
              icon="🗺️"
              onClick={() => open('campaigns')}
            />
            <PortalCard
              title={t('admin.dashboard.cards.games.title')}
              description={t('admin.dashboard.cards.games.description')}
              icon="🎮"
              onClick={() => open('games')}
            />
            <PortalCard
              title={t('admin.dashboard.cards.moderation.title')}
              description={t('admin.dashboard.cards.moderation.description')}
              icon="🕹️"
              onClick={() => open('moderation')}
            />
            <PortalCard
              title={t('admin.dashboard.cards.launchMatch.title')}
              description={t('admin.dashboard.cards.launchMatch.description')}
              icon="🚀"
              onClick={() => open('launch-match')}
            />
            <PortalCard
              title={t('admin.dashboard.cards.jsonImporter.title')}
              description={t('admin.dashboard.cards.jsonImporter.description')}
              icon="📥"
              onClick={() => open('json-importer')}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

